import {reactive, watch} from "vue"

class PvOfferModel
{
    subsidyModel = null

    constructor({
                    defaultSolarPanelId,
                    defaultInverterId,
                    roofSheathings,
                    priceFactors,
                    userExtraPremium,
                    offerBasis,
                    samplePvOfferIndex,
                    allBatteries,
                    allSolarPanels,
                    allInverters,
                    minimumCompanyMargins,
                    baseMargins,
                    annualConsumption,
                    dynamicConsumptionMode,
                    enablePowerOptimizers,
                    powerOptimizersAuto,
                    groundMounted,
                    discount,
                    autoFillOptimizers,
                    extraCosts,
                    subsidyModel,
                    financingModel,
                })
    {
        if (subsidyModel) {
            this.subsidyModel = subsidyModel
        }
        if (financingModel) {
            this.financingModel = financingModel
        }

        if (discount === undefined) {
            discount = 0
        }

        if (enablePowerOptimizers) {
            enablePowerOptimizers = true;
        } else {
            enablePowerOptimizers = false
        }
        if (powerOptimizersAuto) {
            this.powerOptimizersAuto = true;
        } else {
            this.powerOptimizersAuto = false
        }


        groundMounted = !!groundMounted;

        this.dynamicConsumptionMode = dynamicConsumptionMode
        this.minimumCompanyMargins = minimumCompanyMargins
        this.samplePvOfferIndex = samplePvOfferIndex
        this.allBatteries = allBatteries
        this.allInverters = allInverters
        this.baseMargins = baseMargins
        this.autoFillOptimizers = autoFillOptimizers === true;

        let solarPanelSelected = allSolarPanels.find((panel) => {
            return panel.id === defaultSolarPanelId;
        })

        let recommendedPanelNumber = this.getRecommendedPanelsNumber(solarPanelSelected, annualConsumption)

        let inverterSelected

        if (defaultInverterId === null) {
            inverterSelected = this.getMinSuitableInverter(1, recommendedPanelNumber, solarPanelSelected.power_output);
        } else {
            inverterSelected = allInverters.find((inverter) => {
                return inverter.id === defaultInverterId;
            })
        }

        if (extraCosts === undefined) {
            extraCosts = []
        }

        this.$data = reactive({
            enablePowerOptimizers: enablePowerOptimizers,
            pvSolarPanelsNumber: recommendedPanelNumber,
            forceCommissionVisibility: false,
            solarPanelSelected: solarPanelSelected,
            inverterSelected: inverterSelected,
            batterySelected: null,
            strings: 2,
            discount: discount,
            optimizersNumber: this.getOptimizersRecommendedNumber(),
            inverterQuantity: 1,
            taxIncentivePercent: 0.17,
            offerBasis: offerBasis,
            annualConsumption: annualConsumption,
            groundMounted: groundMounted,
            extraCosts: extraCosts,
            userExtraPremium: userExtraPremium,
        })

        watch(
            () => ({ ...this.$data }),
            (newData, oldData) => {
                if (newData.annualConsumption !== oldData.annualConsumption) {
                    let recommendedPanelNumber = this.getRecommendedPanelsNumber(
                        this.$data.solarPanelSelected, newData.annualConsumption
                    )
                    this.$data.recommendedPanelNumber = recommendedPanelNumber

                    this.$data.inverterSelected = this.getMinSuitableInverter(
                        1, recommendedPanelNumber, this.$data.solarPanelSelected.power_output
                    );
                }
            }
        )

        this.priceFactors = Object.assign(
            {},
            ...priceFactors.map((o) => {
                let key = o.name_as_variable;
                let value = parseFloat(o.price);

                return { [key]: value };
            })
        )
        this.priceFactorsOriginal = priceFactors

        this.roofSheathings = roofSheathings

        if (offerBasis.roof_sheathing === null) {
            let defaultRootSheathing = this.getDefaultRootSheathing()
            if (defaultRootSheathing && defaultRootSheathing.id) {
                offerBasis.roof_sheathing = this.getDefaultRootSheathing().id
            } else {
                console.error('No default roof sheathing is set')
                offerBasis.roof_sheathing = 1
            }
        }

        this.changeInverter(inverterSelected)
    }

    getRecommendedPanelsNumber(solarPanel, annualConsumption)
    {
        if (solarPanel === undefined) {
            solarPanel = this.$data.solarPanelSelected
        }
        if (annualConsumption === undefined) {
            annualConsumption = this.$data.annualConsumption
        }
        // power_output
        return Math.ceil(annualConsumption / solarPanel.power_output)
    }

    getPanelNumber()
    {
        if (this.dynamicConsumptionMode) {
            this.$data.pvSolarPanelsNumber = this.getRecommendedPanelsNumber()
        }

        return this.$data.pvSolarPanelsNumber
    }

    changeSolarPanel(solarPanel)
    {
        this.$data.solarPanelSelected = solarPanel;
    }

    changeBattery(battery)
    {
        this.$data.batterySelected = battery;
    }

    changeInverter(inverter)
    {
        this.$data.inverterSelected = inverter;

        this.$data.optimizersNumber = this.getOptimizersRecommendedNumber()
        this.$data.strings = 1;

        this.$data.inverterQuantity = 1;
        if (!this.batteriesCompatible().includes(this.$data.batterySelected)) {
            this.$data.batterySelected = null;
        }
    }

    getOptimizersRecommendedNumber()
    {
        if (this.$data && this.$data.inverterSelected && this.areOptimizersAvailable()) {
            if(this.powerOptimizersAuto) {
                return this.getPanelNumber();
            } else {
                if(this.$data.inverterSelected.brand === "SolarEdge") {
                    return this.getPanelNumber();
                } else {
                    return 0;
                }
            }
        } else {
            return 0
        }
    }

    getOptimizersNumber()
    {
        if (this.powerOptimizersAuto) {
            return this.getOptimizersRecommendedNumber()
        } else {
            return this.$data.optimizersNumber
        }
    }

    getDefaultRootSheathing()
    {
        return this.roofSheathings.find(
            (roofSheathing) => roofSheathing.is_default
        )
    }

    getRoofSheathingSelected()
    {
        if (this.$data.groundMounted) {
            return this.getRoofSheathingByKey('ground')
        }

        return this.roofSheathings.find(
            (roofSheathing) => roofSheathing.id === this.$data.offerBasis.roof_sheathing
        )
    }

    isRoofSheathingKeyAvailable(key)
    {
        return this.getRoofSheathingByKey(key) !== undefined
    }

    getRoofSheathingByKey(key)
    {
        return this.roofSheathings.find(
            (roofSheathing) => roofSheathing.key === key
        )
    }

    getPriceDiffToDefault(key)
    {
        let defaultPrice = this.getMountingMaterialPrice(this.getDefaultRootSheathing());
        let groundPrice = this.getMountingMaterialPrice(this.getRoofSheathingByKey(key));
        return groundPrice - defaultPrice
    }

    getMountingMaterialPrice(sheathing)
    {
        if (sheathing === undefined) {
            sheathing = this.getRoofSheathingSelected()
        }

        return this.getPanelNumber() * parseFloat(sheathing.price_per_module)
            + parseFloat(sheathing.extra_price_per_installation)
    }

    getInstallerCommissionPrice()
    {
        const installationSize = this.installationSize();
        let costPerKwp;
        if (installationSize < 5) {
            costPerKwp = this.priceFactors.installerCommissionCostPerKwpLessThanSizeFive;
        } else if (installationSize < 15) {
            costPerKwp = this.priceFactors.installerCommissionCostPerKwpLessThanSizeFifteen;
        } else if (installationSize < 50) {
            costPerKwp = this.priceFactors.installerCommissionCostPerKwpLessThanSizeFifty;
        } else {
            costPerKwp = this.priceFactors.installerCommissionCostPerKwpGreaterOrEqualThanSizeFifty;
        }

        return installationSize * costPerKwp;
    }

    getDcBoxPrice()
    {
        if (this.$data.offerBasis.lightning_protection) {
            return this.$data.strings === 1
                ? this.priceFactors.dcBoxPriceOneStringWithLightningProtection
                : this.priceFactors.dcBoxPriceTwoStringsWithLightningProtection;
        } else {
            return this.$data.strings === 1
                ? this.priceFactors.dcBoxPriceOneString
                : this.priceFactors.dcBoxPriceTwoStrings;
        }
    }

    getOptimizersPrice()
    {
        let price = 0;
        if (this.$data.inverterSelected.brand === "Huawei") {
            price = (this.getOptimizersNumber() * this.priceFactors.powerOptimizerHuaweiPrice);
        } else if (this.$data.inverterSelected.brand === "SolarEdge") {
            if (this.$data.solarPanelSelected.power_output <= 380) {
                price =  (this.getOptimizersNumber() * this.priceFactors.powerOptimizerSEPriceSmall);
            } else {
                price = this.getOptimizersNumber() * this.priceFactors.powerOptimizerSEPriceLarge
            }
        }

        return price;
    }

    getTotalPriceDiffToOptimizers()
    {
        const currentPrice = Math.abs(this.totalPriceWithMarginWithVAT(false)).toFixed(0)
        const newPrice = Math.abs(this.totalPriceWithMarginWithVAT(true)).toFixed(0)
        return newPrice - currentPrice
    }

    getExtraCostsPrice()
    {
        return this.$data.extraCosts.reduce((sum, extraCost) => {
            let amount = 0;
            if (extraCost.selected) {
                amount = extraCost.price
            }

            return sum + amount;
        }, 0);
    }

    finalOfferData() {
        const extras = this.extras();

        return {
            baseSellingPriceNet: this.baseSellingPriceNet(),
            sellingPriceNet: this.totalPriceNet(),
            discount: this.$data.discount,
            salesCommission: this.newSalesCommission(),
            roofType: this.$data.offerBasis.roof_sheathing,
            annualOutput: this.installationSize() * 1.02,
            panelsNumber: this.getPanelNumber(),
            co2savings: Math.round(1.36 * 25 * this.installationSize()),
            optimizersNumber: this.$data.enablePowerOptimizers ? this.getOptimizersNumber() : 0,
            optimizerImage: this.$data.inverterSelected && this.$data.inverterSelected.brand ? `img/offer/optimizer_${this.$data.inverterSelected.brand.toLowerCase()}.png` : 'error',
            image1:
                this.$data.offerBasis.imageUpload &&
                this.$data.offerBasis.imageUpload.length > 0
                    ? this.$data.offerBasis.imageUpload[0]
                    : null,
            image2:
                this.$data.offerBasis.imageUpload &&
                this.$data.offerBasis.imageUpload.length > 1
                    ? this.$data.offerBasis.imageUpload[1]
                    : null,
            solarPanel: this.$data.solarPanelSelected.id,
            inverter: this.$data.inverterSelected.id,
            inverter_quantity: this.$data.inverterQuantity,
            pdId: this.$data.offerBasis.pdId,
            strings: this.$data.strings,
            vatLevel: this.$data.offerBasis.vat_level,
            extras: extras,
            extraCostsSelected: this.getExtraCostsSelected(),
            priceFactorsSelected: this.getPriceFactorsSelected(extras),
            battery: this.$data.batterySelected ? this.$data.batterySelected.id : null,
        };
    }

    batteriesCompatible()
    {
        if (this.$data.inverterSelected.is_hybrid) {
            const compatibleBatteryBrands = JSON.parse(this.$data.inverterSelected.compatible_battery_brands);

            if (compatibleBatteryBrands) {
                return this.allBatteries.filter((bat) => {
                    return compatibleBatteryBrands.includes(bat.brand);
                });
            } else {
                return [];
            }
        } else {
            return [];
        }
    }

    totalNetCosts(optimizersEnabled)
    {
        let obj = this.prices(optimizersEnabled);

        return Object.keys(obj).reduce(
            (sum, key) => sum + parseFloat(obj[key] || 0),
            0
        );
    }

    minimumCompanyMargin()
    {
        const installationSize = this.installationSize()

        for (let x in this.minimumCompanyMargins) {
            if (
                installationSize >= this.minimumCompanyMargins[x].range_from &&
                installationSize < this.minimumCompanyMargins[x].range_to
            ) {
                return (
                    installationSize * this.minimumCompanyMargins[x].factor
                );
            }
        }

        // FALLBACK
        return this.installationSize() * 300;
    }

    minimumSellingPriceNet()
    {
        return this.totalNetCosts() + this.minimumCompanyMargin() +
            (this.$data.batterySelected ? this.$data.batterySelected.selling_price : 0)
    }

    baseMargin()
    {
        const installationSize = this.installationSize()

        for (let x in this.baseMargins) {
            if (
                installationSize >=
                this.baseMargins[x].range_from &&
                installationSize < this.baseMargins[x].range_to
            ) {
                return this.baseMargins[x].factor;
            }
        }

        // FALLBACK
        return 1.25;
    }

    totalMarkup()
    {
        return this.totalPriceNet() - this.minimumSellingPriceNet()
    }

    newSalesCommission()
    {
        return this.totalMarkup() * 0.5 + this.batteryCommissions().sales
    }

    newCompanyProfit()
    {
        return this.totalMarkup() * 0.5 + this.minimumCompanyMargin() + this.batteryCommissions().company
    }

    totalPriceNet(optimizersEnabled, discount)
    {
        if (discount === undefined) {
            discount = this.$data.discount
        }

        if (optimizersEnabled === undefined) {
            optimizersEnabled = this.$data.enablePowerOptimizers
        }

        return this.baseSellingPriceNet(optimizersEnabled) - discount
    }

    ownInvestmentTotal(discount)
    {
        if (discount === undefined) {
            discount = this.$data.discount
        }

        let installationSize = this.installationSize()
        return this.totalPriceWithMarginWithVAT(undefined, discount) - this.subsidyModel.getSubsidyAmount({ installationSize })
    }

    baseSellingPriceNet(optimizersEnabled)
    {
        return this.totalNetCosts(optimizersEnabled) * this.baseMargin() +
            this.$data.userExtraPremium +
            (this.$data.batterySelected
                ? this.$data.batterySelected.selling_price
                : 0)
    }

    legacySalesCommission()
    {
        return this.totalPriceNet() * 0.08
    }

    legacyCompanyProfit()
    {
        return this.totalPriceNet() -
            this.legacySalesCommission() -
            this.totalNetCosts()
    }

    installationSize(pvSolarPanelsNumber, powerOutput)
    {
        if (pvSolarPanelsNumber === undefined) {
            pvSolarPanelsNumber = this.getPanelNumber()
        }
        if (powerOutput === undefined) {
            powerOutput = this.$data.solarPanelSelected.power_output
        }

        return (pvSolarPanelsNumber *  powerOutput) / 1000
    }

    getMinSuitableInverter(inverterQuantity, pvSolarPanelsNumber, powerOutput)
    {
        if (inverterQuantity === undefined) {
            inverterQuantity = this.$data.inverterQuantity
        }

        const suitableInverters = this.suitableInverters(inverterQuantity, pvSolarPanelsNumber, powerOutput);

        if (suitableInverters.length === 0) {
            return [];
        }

        return suitableInverters.reduce((prev, cur) => (parseFloat(cur.max_kwp) < parseFloat(prev.max_kwp) ? cur : prev));
    }

    suitableInverters(inverterQuantity, pvSolarPanelsNumber, powerOutput)
    {
        if (inverterQuantity === undefined) {
            inverterQuantity = this.$data.inverterQuantity
        }

        return this.allInverters.filter((inverter) => {
            const installationSize = this.installationSize(pvSolarPanelsNumber, powerOutput);
            return (
                parseFloat(inverter.max_kwp) * inverterQuantity > installationSize
                &&
                inverter.kw < (installationSize + 1)
            );
        });
    }

    areOptimizersAvailable()
    {
        if (this.$data.inverterSelected) {
            return (
                this.$data.inverterSelected.brand === "Huawei" ||
                this.$data.inverterSelected.brand === "SolarEdge"
            );
        }
        return true;
    }

    extras()
    {
        const extras = {};
        const pricing = {};
        const meta = {};
        const extraPrices = this.prices();

        for (let x in extraPrices) {
            pricing[x] = !isNaN(extraPrices[x]) ? extraPrices[x] : 0;
        }

        pricing["userExtraPremium"] = this.$data.userExtraPremium;
        pricing["totalNetCosts"] = this.totalNetCosts();
        pricing["minimumCompanyMargin"] = this.minimumCompanyMargin();
        pricing["baseMargin"] = this.baseMargin();
        pricing["minimumSellingPriceNet"] = this.minimumSellingPriceNet();
        pricing["totalMarkup"] = this.totalMarkup();
        pricing["newCompanyProfit"] = this.newCompanyProfit();
        pricing["legacySalesCommission"] = this.legacySalesCommission();
        pricing["legacyCompanyProfit"] = this.legacyCompanyProfit();
        pricing["installationSize"] = this.installationSize();
        pricing["totalPriceWithMarginWithVAT"] =this.totalPriceWithMarginWithVAT();
        pricing["battery"] = this.$data.batterySelected ? this.$data.batterySelected.buying_price : null;

        meta["samplePvOfferIndex"] = this.samplePvOfferIndex;
        meta["lightningProtection"] = this.$data.offerBasis.lightning_protection;
        meta["diggingLength"] = this.$data.offerBasis.digging_length;
        meta["forceCommissionVisibility"] = this.$data.forceCommissionVisibility;

        extras["pricing"] = pricing;
        extras["subsidies"] = [this.getSubsidy()];
        extras["financing"] = this.getFinancing();
        extras["meta"] = meta;
        extras["public"] = true;

        return extras;
    }


    prices(optimizersEnabled)
    {
        if (optimizersEnabled === undefined) {
            optimizersEnabled = this.$data.enablePowerOptimizers
        }

        return {
            extraCosts: this.getExtraCostsPrice(),
            solarPanels: this.$data.solarPanelSelected.price * this.getPanelNumber(),
            inverter: parseInt(this.$data.inverterSelected.price) * this.$data.inverterQuantity,
            mountingMaterial: this.getMountingMaterialPrice(),
            installerCommission: this.getInstallerCommissionPrice(),
            cablesAndAccessories: this.installationSize() * this.priceFactors.cablesPerKw,
            acBoxPrice: this.$data.offerBasis.lightning_protection
                ? this.priceFactors.acBoxPriceWithLightningProtection
                : this.priceFactors.acBoxPrice,
            dcBoxPrice: this.getDcBoxPrice(),
            optimizers: optimizersEnabled ? this.getOptimizersPrice() : 0,
            digging: this.$data.offerBasis.digging
                ? this.$data.offerBasis.digging_length *
                this.priceFactors.diggingPerMeter
                : 0,
            lift: this.$data.offerBasis.lift
                ? this.priceFactors.lift
                : 0,
            insurance: this.$data.offerBasis.insurance
                ? this.priceFactors.insurance
                : 0,
            grounding: this.$data.offerBasis.grounding
                ? this.priceFactors.grounding
                : 0,
            fireProtection: this.installationSize() >= 6.5
                ? this.priceFactors.fireProtection
                : 0,
            stuckTiles: this.$data.offerBasis.stuck_tiles
                ? this.installationSize() * this.priceFactors.stuckTilesPerKwp
                : 0,
            snowGuardRemoval: this.$data.offerBasis.snow_guards
                ? this.installationSize() *
                this.priceFactors.snowGuardRemovalPerKwp
                : 0,
            lightningRodRemoval: this.$data.offerBasis.lightning_rod_removal
                ? this.installationSize() *
                this.priceFactors.lightningRodRemovalPerKwp
                : 0,
            transport: this.priceFactors.transport,
            referral: this.priceFactors.referral,
            // battery: (this.$data.batterySelected. ? this.$data.batterySelected..buying_price : 0)
        };
    }

    getVatAmount()
    {
        return this.totalPriceNet() * (this.$data.offerBasis.vat_level - 1);
    }

    totalPriceWithMarginWithVAT(optimizersEnabled, discount)
    {
        if (optimizersEnabled === undefined) {
            optimizersEnabled = this.$data.enablePowerOptimizers
        }

        if (discount === undefined) {
            discount = this.$data.discount
        }

        return this.totalPriceNet(optimizersEnabled, discount) * this.$data.offerBasis.vat_level
    }

    batteryCommissions()
    {
        const rate = 0.1666;
        return {
            sales: !this.$data.batterySelected
                ? 0
                : (this.$data.batterySelected.selling_price -
                    this.$data.batterySelected.buying_price) *
                rate,
            company: !this.$data.batterySelected
                ? 0
                : (this.$data.batterySelected.selling_price -
                    this.$data.batterySelected.buying_price) *
                (1 - rate),
        };
    }

    getExtraCostsSelected()
    {
        const extraCostsSelected = this.$data.extraCosts.filter(function (extraCost) {
            return extraCost.selected
        })

        return extraCostsSelected.map(a => a.id)
    }

    getPriceFactorsSelected(extras)
    {
        const priceFactorsSelected = this.priceFactorsOriginal.filter(function (priceFactor) {
            if (priceFactor.name_as_variable === 'cablesPerKw' && extras.pricing.cablesAndAccessories > 0)  { return true }
            if (priceFactor.name_as_variable === 'diggingPerMeter' && extras.pricing.digging > 0)  { return true }
            if (priceFactor.name_as_variable === 'lift' && extras.pricing.lift > 0)  { return true }
            if (priceFactor.name_as_variable === 'grounding' && extras.pricing.grounding > 0)  { return true }
            if (priceFactor.name_as_variable === 'fireProtection' && extras.pricing.fireProtection > 0)  { return true }
            if (priceFactor.name_as_variable === 'stuckTilesPerKwp' && extras.pricing.stuckTiles > 0)  { return true }
            if (priceFactor.name_as_variable === 'snowGuardRemovalPerKwp' && extras.pricing.snowGuardRemoval > 0)  { return true }
            if (priceFactor.name_as_variable === 'lightningRodRemovalPerKwp' && extras.pricing.lightningRodRemoval > 0)  { return true }
            if (priceFactor.name_as_variable === 'transport' && extras.pricing.transport > 0)  { return true }
            if (priceFactor.name_as_variable === 'referral' && extras.pricing.referral > 0)  { return true }
            if (priceFactor.name_as_variable === 'acBoxPrice' && extras.pricing.acBoxPrice > 0)  { return true }
            if (priceFactor.name_as_variable === 'insurance' && extras.pricing.insurance > 0) { return true }

            return false;
        })

        return priceFactorsSelected.map(a => a.id);
    }

    getSubsidy()
    {
        if (this.subsidyModel === null) {
            return {
                exists: false
            }
        }

        let installationSize = this.installationSize()

        return this.subsidyModel.getSelectedSubsidy({ installationSize })
    }

    getFinancing()
    {
        if (this.financingModel === null) {
            return {
                available: false
            }
        }

        let grossPrice = this.totalPriceWithMarginWithVAT()
        let netPrice = this.totalPriceNet()
        let subsidy = this.getSubsidy();
        if (subsidy.exists) {
            grossPrice = grossPrice - subsidy.amount
            netPrice = netPrice - subsidy.amount
        }

        return this.financingModel.getSelectedFinancing(grossPrice, netPrice)
    }
}

export default PvOfferModel
